// src/pages/etudiant/AjouterProduit.jsx
import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { config } from '../../config';
import { Link, useNavigate, useParams } from 'react-router-dom';
import './boutiques.css';

export default function AjouterProduit() {
    const { id } = useParams(); // ID de la boutique
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [boutique, setBoutique] = useState(null);
    const [formData, setFormData] = useState({
        nom_produit: '',
        description: '',
        prix: '',
        quantite: '',
        photo_produit: null
    });
    const [photoPreview, setPhotoPreview] = useState(null);

    const API_BASE = config.boutiqueApiBase || `${config.apiBase}/boutiques`;
    const getToken = () => localStorage.getItem('token');

    // Récupérer les infos de la boutique
    useEffect(() => {
        const fetchBoutique = async () => {
            try {
                const token = getToken();
                if (!token) {
                    toast.error('Veuillez vous reconnecter');
                    return;
                }

                const response = await fetch(`${API_BASE}/detail.php?id=${id}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });

                const data = await response.json();
                if (data.success) {
                    setBoutique(data.boutique);
                } else {
                    toast.error('Boutique non trouvée');
                    navigate('/etudiant/boutiques');
                }
            } catch (error) {
                console.error('Erreur:', error);
                toast.error('Erreur de chargement');
            }
        };
        fetchBoutique();
    }, [id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handlePhotoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Vérifier la taille (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                toast.error('La photo est trop volumineuse (max 5MB)');
                return;
            }
            
            // Vérifier le type
            const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
            if (!allowedTypes.includes(file.type)) {
                toast.error('Format non supporté. Utilisez JPG, PNG, GIF ou WEBP');
                return;
            }
            
            setFormData({ ...formData, photo_produit: file });
            
            // Aperçu
            const reader = new FileReader();
            reader.onloadend = () => {
                setPhotoPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!formData.nom_produit.trim()) {
            toast.error('Veuillez entrer un nom de produit');
            return;
        }

        if (!formData.prix || parseFloat(formData.prix) <= 0) {
            toast.error('Veuillez entrer un prix valide');
            return;
        }

        if (!formData.quantite || parseInt(formData.quantite) < 0) {
            toast.error('Veuillez entrer une quantité valide');
            return;
        }

        setLoading(true);
        try {
            const token = getToken();
            if (!token) {
                toast.error('Veuillez vous reconnecter');
                navigate('/login');
                return;
            }

            // ✅ Créer FormData pour l'envoi
            const formDataToSend = new FormData();
            formDataToSend.append('id_boutique', id);
            formDataToSend.append('nom_produit', formData.nom_produit);
            formDataToSend.append('description', formData.description || '');
            formDataToSend.append('prix', formData.prix);
            formDataToSend.append('quantite', formData.quantite || 0);
            
            if (formData.photo_produit) {
                formDataToSend.append('photo_produit', formData.photo_produit);
            }

            const response = await fetch(`${API_BASE}/ajouter_produit.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formDataToSend
            });

            const data = await response.json();

            if (data.success) {
                toast.success('✅ Produit ajouté avec succès !');
                navigate(`/etudiant/boutique/${id}`);
            } else {
                toast.error(data.message || 'Erreur lors de l\'ajout');
            }
        } catch (error) {
            console.error('❌ Erreur:', error);
            toast.error('Erreur: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    if (!boutique) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="shop-page shop-product-editor">
            <Link className="shop-back" to={`/etudiant/boutique/${id}`}>← Retour à la boutique</Link>
            <div className="shop-form shop-product-form">
                <div className="flex items-center gap-3 mb-6">
                    <span className="text-4xl">➕</span>
                    <div>
                        <h1 className="text-3xl font-bold text-gray-800">Ajouter un produit</h1>
                        <p className="text-gray-500">
                            Boutique : <span className="font-semibold text-blue-600">{boutique.nom_boutique}</span>
                        </p>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Nom du produit *
                        </label>
                        <input
                            type="text"
                            name="nom_produit"
                            value={formData.nom_produit}
                            onChange={handleChange}
                            className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Ex: Biscuit Choco"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Description
                        </label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            rows="3"
                            placeholder="Description du produit..."
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Prix (FCFA) *
                            </label>
                            <input
                                type="number"
                                name="prix"
                                value={formData.prix}
                                onChange={handleChange}
                                className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="0"
                                min="0"
                                step="0.01"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Quantité *
                            </label>
                            <input
                                type="number"
                                name="quantite"
                                value={formData.quantite}
                                onChange={handleChange}
                                className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="0"
                                min="0"
                                required
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Photo du produit
                        </label>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handlePhotoChange}
                            className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        {photoPreview && (
                            <div className="mt-3">
                                <img src={photoPreview} alt="Aperçu" className="h-32 rounded-lg object-contain border" />
                            </div>
                        )}
                        <p className="text-xs text-gray-400 mt-1">Format JPG, PNG, GIF ou WEBP (max 5MB)</p>
                    </div>

                    <div className="flex gap-3 pt-4">
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex-1 bg-blue-600 text-white py-3 rounded-xl hover:bg-blue-700 transition font-semibold flex items-center justify-center gap-2"
                        >
                            {loading ? (
                                <>
                                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                                    Ajout en cours...
                                </>
                            ) : (
                                '➕ Ajouter le produit'
                            )}
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate(`/etudiant/boutique/${id}`)}
                            className="px-6 py-3 rounded-xl bg-gray-200 text-gray-700 hover:bg-gray-300 transition"
                        >
                            Annuler
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
